import { chartColorPallet as DEFAULT_COLORS } from '@/config/colorPallets/universalDesignSystem';
// Lookup a default color at the
// specified index of a color pallet
// and loop over them in succession
export function getColorAtPalletIndex(index) {
  let colorPallet = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_COLORS;
  const colorsLen = colorPallet.length;
  return colorPallet[index % colorsLen];
} // Encode an array of colors into a string

export function encodeColors(colors) {
  return (Array.isArray(colors) ? colors : [colors || '']).join('|');
} // Decode a string of encoded colors into a list

export function decodeColors(colors) {
  return `${colors || ''}`.trim().split('|');
} // Convert an rgb color instance to a
// string with optional alpha

export function rgbToString(rgb) {
  const hasAlpha = rgb.a !== undefined;
  return `${hasAlpha ? 'rgba' : 'rgb'}(${rgb.r},${rgb.g},${rgb.b}${hasAlpha ? ',' : ''}${hasAlpha ? rgb.a : ''})`;
} // Convert HEX and HSL values to RGBA
// Source: https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb#answer-74662179
// Example RGB: rgb(0, 77, 229)

export function toRGB(color) {
  if (false) return '';
  const {
    style
  } = new Option();
  style.color = color;
  return style.color;
} // Conver color to RGBA object

export function toRGBA(color) {
  const rgb = toRGB(color);
  const values = rgb.match(/\d+/g);
  if (!values) return {
    r: 0,
    g: 0,
    b: 0
  }; // Default alpha to 1 if not provided

  const a = isNaN(+values[3]) ? 1 : +values[3];
  return {
    r: +values[0],
    g: +values[1],
    b: +values[2],
    a: a
  };
} // Compare two colors for equality

export const compareColors = (color1, color2) => {
  return toRGB(color1) === toRGB(color2);
}; // Convert HEX and RGB values to transparent RGBA

export function transparentize(color, opacity) {
  const rgb = toRGB(color);
  const rgba = rgb.replace(')', `, ${opacity})`).replace('rgb', 'rgba');
  return rgba;
} // Get the brightness of a color
// http://www.w3.org/TR/AERT#color-contrast

export function getBrightness(color) {
  const rgb = toRGB(color); // Extract the RGB values
  // Coverts rgb(0, 77, 229) -> [0, 77, 229]

  const values = rgb.match(/\d+/g);
  if (!values) return -1;
  const r = +values[0];
  const g = +values[1];
  const b = +values[2];
  return (r * 299 + g * 587 + b * 114) / 1000;
} // Determine if a color is dark

export function isDark(color) {
  return getBrightness(color) < 128;
} // Convert color to HEX format

export function toHex(color) {
  let preserveAlpha = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  if (!color) return color;
  let hexColor;
  let hasAlpha = false;
  let alphaValue = null; // Check if it's RGB/RGBA format

  if (color.toLowerCase().includes('rgba')) {
    hasAlpha = true;
    const numbers = color.match(/[\d.]+/g);

    if (numbers && numbers.length >= 4) {
      alphaValue = parseFloat(numbers[3]);
    }

    hexColor = rgbToHex(color);
  } else if (color.toLowerCase().includes('rgb')) {
    hexColor = rgbToHex(color);
  } else if (color.includes(',')) {
    // check for comma-separated values
    const values = color.split(',').map(v => v.trim());

    if (values.length >= 4) {
      hasAlpha = true;
      alphaValue = parseFloat(values[3]);
    }

    hexColor = rgbToHex(`rgb${hasAlpha ? 'a' : ''}(${color})`);
  } else {
    hexColor = color.toUpperCase().startsWith('#') ? color.toUpperCase() : `#${color.toUpperCase()}`;
  } // Return name with alpha info if needed


  if (hexColor && hasAlpha && preserveAlpha && alphaValue !== null) {
    const alphaPercentage = Math.round(alphaValue * 100);
    return `${hexColor}` + (alphaPercentage === 100 ? '' : ` (${alphaPercentage}%)`);
  }

  return hexColor || color;
} // Convert RGB/RGBA color to HEX (ignores alpha channel)

export function rgbToHex(color) {
  // Extract numbers from RGB/RGBA string
  const numbers = color.match(/[\d.]+/g);

  if (!numbers || numbers.length < 3) {
    return color; // Return original if can't parse
  } // Take only RGB values (ignore alpha if present)


  const [r, g, b] = numbers.slice(0, 3).map(num => parseInt(num, 10)); // Validate RGB values

  if (r > 255 || g > 255 || b > 255 || r < 0 || g < 0 || b < 0) {
    return color; // Return original if invalid
  } // Convert to HEX


  const toHex = n => n.toString(16).padStart(2, '0').toUpperCase();

  let hexColor = `#${toHex(r)}${toHex(g)}${toHex(b)}`; // Handle alpha channel if present

  if (numbers.length >= 4) {
    const alphaValue = parseFloat(numbers[3]);
    let alpha; // Determine if alpha is in 0-1 range or 0-255 range

    if (alphaValue <= 1) {
      // Alpha is in 0-1 range, convert to 0-255
      alpha = Math.round(alphaValue * 255);
    } else {
      // Alpha is already in 0-255 range
      alpha = Math.round(alphaValue);
    } // Validate alpha value


    if (alpha >= 0 && alpha < 255) {
      hexColor += toHex(alpha);
    }
  }

  return hexColor;
}